import { Attribute, Relationship, Resource } from 'json-api';
import { Translatable } from '../../translation/translatable';
import { AtomResource } from '../classes/atom-resource';
import { CustomField, CustomFieldConfig } from './custom-field';
import { Job } from './job.resource';
import { Offer } from './offer.resource';
import { Capping } from './capping.resource';
import { Message } from './message.resource';
import { Fraudplatform } from './fraudplatform.resource';
import { Pack } from './pack.resource';
import {
  PlatformCapability,
  PlatformCapabilityConfig,
} from './platformcapability.resource';
import {
  NumberFilter,
  StringFilter,
  ResourceListFilter,
  BooleanFilter,
} from 'src/app/filter/filter';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import { AtomService } from '../atom.service';
import { Activity } from './activity.resource';
import { FirmJobConfig } from 'src/app/shared/tools/search/search-config';
import { Notificationmanagementsdvp4m } from './notification.resource';
import { Ussdmenus } from './ussd-menu.ressource';
import { ErrorcodeConfig } from './errorcodes.resource';

@Resource({
  type: 'platforms',
})
export class Platform extends AtomResource {
  @Attribute() name?: Translatable;
  @Attribute() desc?: string;
  @Attribute() payment?: boolean;
  @Attribute() messaging?: boolean;
  @Attribute({ key: 'header_enrichment' }) headerEnrichment?: boolean;
  @Attribute() iplookup?: boolean;
  @Attribute() msisdnlookup?: boolean;
  @Attribute() fraud?: boolean;
  @Attribute({ key: 'otp_identification' }) otpIdentification?: boolean;
  @Attribute({ key: 'alias_conversion' }) aliasConversion?: boolean;
  @Attribute({ key: 'msisdn_conversion' }) msisdnConversion?: boolean;
  @Attribute() data?: Record<string, unknown>;
  @Attribute({ key: 'external_id_name' }) externalIdName?: string;

  @Relationship() job?: Job;
  @Relationship() offers?: Offer[];
  @Relationship() cappings?: Capping[];
  @Relationship() messages?: Message;
  @Relationship() fraudplatforms?: Fraudplatform[];
  @Relationship() customfields?: CustomField[];
  @Relationship() enrichments?: Pack[];
  @Relationship() msisdnlookups?: Pack[];
  @Relationship() iplookups?: Pack[];
  @Relationship() otppacks?: Pack[];
  @Relationship() platformcapabilities?: PlatformCapability[];
  @Relationship() errorcodes?: PlatformCapability[];
  @Relationship() notificationmanagementsdvp4m?: Notificationmanagementsdvp4m[];
  @Relationship() ussdmenus?: Ussdmenus[];
}

export class PlatformConfig implements ResourceConfig {
  tables = {
    includes: [
      'job.firm.headquarterscountry',
      'job.activity',
      'customfields',
      'platformcapabilities.enduserproviderjob.firm.headquarterscountry',
    ],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main', translatable: false }],
        header: 'ID',
        translatable: false,
        displayed: true,
        sort: 'id',
      },
      {
        columnDef: 'name',
        displayedDatas: [
          {
            path: ['name'],
            class: 'mat-body-2',
          },
          {
            path: ['job.activity.name'],
            class: 'mat-body-2',
          },
        ],
        header: 'Name',
        displayed: true,
        sort: 'name',
        link: {
          detail: true,
        },
      },
      {
        columnDef: 'firm',
        displayedDatas: [
          {
            path: ['job.firm.name'],
            class: 'mat-body-2',
            translatable: true,
          },
          {
            path: [
              'job.firm.headquarterscountry.id',
              'job.firm.headquarterscountry.name',
            ],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        header: 'Firm',
        translatable: true,
        displayed: true,
        link: {
          resource: 'firms',
          filter: 'firm.id',
          icon: 'business',
        },
        options: {
          flag: 'job.firm.headquarterscountry.id',
        },
        sort: 'firm.name',
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        translatable: false,
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'dns',
      title: [
        {
          path: ['name'],
        },
      ],
      subtitle: [
        {
          path: ['job.firm.headquarterscountry.id', 'job.activity.name'],
        },
      ],
      validity: true,
    },
    elements: [
      {
        showEmpty: true,
        columns: true,
        list: [
          {
            icon: 'language',
            title: 'Firm',
            APIKey: 'job',
            value: {
              path: ['job.firm.name'],
            },
            link: {
              resource: 'firms',
              filter: 'job.firm.id',
            },
            options: {
              flag: 'job.firm.headquarterscountry.id',
            },
          },
          {
            icon: 'business',
            title: 'Activity',
            APIKey: 'job',
            value: {
              path: ['job.activity.name'],
            },
          },
        ],
      },
      {
        title: 'External ID Label',
        showEmpty: true,
        list: [
          {
            APIKey: 'externalIdName',
            value: {
              path: ['externalIdName'],
            },
          },
        ],
      },
    ],
    tabs: [
      {
        title: 'Capacities',
        elements: [
          {
            chips: [
              {
                title: 'Payment',
                APIKey: 'payment',
                value: {
                  path: ['payment'],
                  type: 'boolean',
                },
              },
              {
                title: 'Messaging',
                APIKey: 'messaging',
                value: {
                  path: ['messaging'],
                  type: 'boolean',
                },
              },
              {
                title: 'Header Enrichment',
                APIKey: 'headerEnrichment',
                value: {
                  path: ['headerEnrichment'],
                  type: 'boolean',
                },
              },
              {
                title: 'IP Lookup',
                APIKey: 'iplookup',
                value: {
                  path: ['iplookup'],
                  type: 'boolean',
                },
              },
              {
                title: 'MSISDN Lookup',
                APIKey: 'msisdnlookup',
                value: {
                  path: ['msisdnlookup'],
                  type: 'boolean',
                },
              },
              {
                title: 'Fraud',
                APIKey: 'fraud',
                value: {
                  path: ['fraud'],
                  type: 'boolean',
                },
              },
              {
                title: 'OTP Identification',
                APIKey: 'otpIdentification',
                value: {
                  path: ['otpIdentification'],
                  type: 'boolean',
                },
              },
              {
                title: 'Alias Conversion',
                APIKey: 'aliasConversion',
                value: {
                  path: ['aliasConversion'],
                  type: 'boolean',
                },
              },
              {
                title: 'MSISDN Conversion',
                APIKey: 'msisdnConversion',
                value: {
                  path: ['msisdnConversion'],
                  type: 'boolean',
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Custom Fields',
        elements: [
          {
            resource: {
              APIKey: 'customfields',
              multiple: true,
              config: new CustomFieldConfig(),
            },
          },
        ],
      },
      {
        title: 'Platform Capabilities',
        elements: [
          {
            resource: {
              APIKey: 'platformcapabilities',
              multiple: true,
              config: new PlatformCapabilityConfig(this.atom),
            },
          },
        ],
      },
      {
        title: 'Error Codes',
        elements: [
          {
            resource: {
              APIKey: 'errorcodes',
              multiple: true,
              config: new ErrorcodeConfig(),
            },
          },
        ],
      },
    ],
  };
  wizard = {
    tabs: [
      {
        title: 'Settings',
        formGroup: 'settings',
        list: [
          {
            type: 'text',
            title: 'Name',
            formControl: 'name',
            APIKey: 'name',
            required: true,
            translatable: true,
          },
          {
            type: 'resource',
            title: 'Firm Job',
            formControl: 'job',
            APIKey: 'job',
            multiple: false,
            required: true,
            search: new FirmJobConfig(this.atom),
          },
          {
            type: 'text',
            title: 'Description',
            formControl: 'desc',
            APIKey: 'desc',
          },
          {
            type: 'text',
            title: 'External ID Label',
            formControl: 'externalIdName',
            APIKey: 'externalIdName',
          },
          {
            type: 'boolean',
            title: 'Payment',
            APIKey: 'payment',
            formControl: 'payment',
          },
          {
            type: 'boolean',
            title: 'Messaging',
            APIKey: 'messaging',
            formControl: 'messaging',
          },
          {
            type: 'boolean',
            title: 'Header Enrichment',
            APIKey: 'headerEnrichment',
            formControl: 'headerEnrichment',
          },
          {
            type: 'boolean',
            title: 'IP Lookup',
            APIKey: 'iplookup',
            formControl: 'iplookup',
          },
          {
            type: 'boolean',
            title: 'MSISDN Lookup',
            APIKey: 'msisdnlookup',
            formControl: 'msisdnlookup',
          },
          {
            type: 'boolean',
            title: 'Fraud',
            APIKey: 'fraud',
            formControl: 'fraud',
          },
          {
            type: 'boolean',
            title: 'OTP Identification',
            APIKey: 'otpIdentification',
            formControl: 'otpIdentification',
          },
          {
            type: 'boolean',
            title: 'Alias Conversion',
            APIKey: 'aliasConversion',
            formControl: 'aliasConversion',
          },
          {
            type: 'boolean',
            title: 'MSISDN Converion',
            APIKey: 'msisdnConversion',
            formControl: 'msisdnConversion',
          },
        ],
      },
      {
        title: 'Custom Fields',
        formGroup: 'customfields',
        list: [
          {
            type: 'ownResource',
            required: false,
            resource: {
              APIKey: 'contacts',
              config: new CustomFieldConfig(),
              multiple: true,
            },
          },
        ],
      },
      {
        title: 'Platform Capabilities',
        formGroup: 'capabilities',
        list: [
          {
            type: 'ownResource',
            required: false,
            resource: {
              APIKey: 'platformcapabilities',
              config: new PlatformCapabilityConfig(this.atom),
              multiple: true,
            },
          },
        ],
      },
    ],
  };
  resource = Platform;
  resourceName = 'Platform';
  resourceKey = 'platforms';
  resourceKeySingle = 'platform';
  includes = [
    'job.firm.headquarterscountry',
    'job.activity',
    'customfields',
    'platformcapabilities.enduserproviderjob.firm.headquarterscountry',
    'errorcodes',
  ];
  filters = [
    new NumberFilter('id', 'ID'),
    new StringFilter('name', 'Name'),
    new NumberFilter('job.firm.id', 'Firm ID'),
    new StringFilter('job.firm.name', 'Firm name'),
    new StringFilter(
      'platformcapabilities.enduserproviderjob.firm.headquarterscountry.id',
      'Country ID',
      true
    ),
    new StringFilter(
      'platformcapabilities.enduserproviderjob.firm.headquarterscountry.name',
      'Country Name'
    ),
    new NumberFilter(
      'platformcapabilities.enduserproviderjob.id',
      'Enduser Provider Job ID'
    ),
    new ResourceListFilter(this.atom, Activity, 'job.activity.id', 'Activity'),
    new BooleanFilter('otp_identification', 'OTP Identification'),
    new BooleanFilter('header_enrichment', 'Header Enrichment'),
    new BooleanFilter('msisdn_conversion', 'MSISDN Conversion'),
    new BooleanFilter('fraud', 'Fraud Detection'),
  ];
  constructor(private atom: AtomService) {}
}
